import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withPreloading,
  withViewTransitions
} from '@angular/router';
import { SafeRouteReuseStrategy } from '@mogenius/core/routing/safe-route-reuse-strategy';
import { appProviderFactory } from '@mogenius/app.provider';
import { appRoutes } from '@mogenius/app.routes';
import { ClusterSocketEventService } from '@mogenius/cluster/services/cluster-socket-event.service';
import { ChunkErrorHandler } from '@mogenius/core/error-handlers/chunk-error-handler';
import { allowedHeaderCleanupInterceptor } from '@mogenius/core/interceptors/allowed-header-cleanup.interceptor';
import { requestErrorInterceptor } from '@mogenius/core/interceptors/request-error.interceptor';
import { requestHeaderInterceptor } from '@mogenius/core/interceptors/request-header.interceptor';
import { AppUpdateService } from '@mogenius/core/services/app-update.service';
import { environment } from '@mogenius/environments/environment';
import { authInterceptor } from '@mogenius/user/interceptors/auth.interceptor';
import { WorkspaceSocketEventService } from '@mogenius/workspace/services/workspace-socket-event.service';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPiwikProModule } from '@piwikpro/ngx-piwik-pro';
import { provideLottieOptions } from 'ngx-lottie';
import { firstValueFrom } from 'rxjs';
import { APP_VERSION } from 'version.token';
import { providePrimeNG } from 'primeng/config';
import MoPreset from '../theme/mo-primeng-theme';
import 'chartjs-adapter-moment';
import 'chartjs-adapter-date-fns';
import { ConfirmationService, FilterService } from 'primeng/api';
import { MoPrimengCustomFilterService } from 'src/theme/service/primeng-custom-filter.service';
import { provideEchartsCore } from 'ngx-echarts';
import { DialogService } from 'primeng/dynamicdialog';
import { MetaTitleService } from '@mogenius/shared/services/meta-title.service';
import { PageHeaderService } from '@mogenius/shared/services/page-header.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    // PrimeNG
    providePrimeNG({
      theme: {
        preset: MoPreset,
        options: {
          prefix: 'mo',
          darkModeSelector: '[data-bs-theme="dark"]',
          cssLayer: false
        }
      },
      ripple: true,
      // Raised when ng-bootstrap modals still sat at z-index 1055 and PrimeNG's
      // default overlay base of 1000 put select/autocomplete/popover panels
      // behind them. ng-bootstrap and ng-select are gone, but these values now
      // carry PrimeNG's own stacking -- overlays above dialogs -- so they stay.
      zIndex: {
        modal: 1100,
        overlay: 1060,
        menu: 1060,
        tooltip: 1100
      }
    }),
    { provide: FilterService, useClass: MoPrimengCustomFilterService },
    DialogService,
    ConfirmationService,
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    // Routing & HTTP
    { provide: RouteReuseStrategy, useClass: SafeRouteReuseStrategy },
    { provide: APP_VERSION, useValue: (window as any).appVersion },
    provideRouter(appRoutes, withViewTransitions(), withPreloading(PreloadAllModules)),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        requestErrorInterceptor,
        requestHeaderInterceptor,
        allowedHeaderCleanupInterceptor
      ])
    ),

    // Translation
    provideTranslateService({ fallbackLang: 'en', lang: 'en' }),
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),

    ...(environment.stage !== 'local'
      ? [
          importProvidersFrom(
            NgxPiwikProModule.forRoot('0058bbd1-0aa2-4a17-94e5-4dccc6310c77', 'https://mogenius.containers.piwik.pro')
          )
        ]
      : []),

    // Core services
    ClusterSocketEventService,
    WorkspaceSocketEventService,

    // Custom app initializer
    provideAppInitializer(() => {
      return firstValueFrom(appProviderFactory());
    }),

    provideAppInitializer(() => {
      inject(AppUpdateService).init();
    }),

    provideAppInitializer(() => {
      inject(PageHeaderService);
    }),

    // App-wide providers

    // Platform-specific
    { provide: Window, useValue: window },

    { provide: TitleStrategy, useExisting: MetaTitleService },

    // ECharts (tree-shaken: only import what we need)
    provideEchartsCore({
      echarts: () => import('./echarts-config')
    }),

    // Lottie
    provideLottieOptions({
      player: () => import('lottie-web')
    })
  ]
};
